import React, { useContext, useMemo } from 'react';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';

/**
 * KPI 进度区域条件渲染守卫
 *
 * 直接比较 filterVal 中的筛选值是否全部等于默认值来决定是否渲染。
 * - rangeTime vs defaultRangeTime（由 PcConsoleDailyFilter 传入）
 * - 维度筛选器 (zone/system/channel/lang/region): 默认值为 ['255']
 *
 * Props:
 *  - showWhenDefault (boolean): true → 仅在默认状态下渲染 children；
 *                                false → 仅在筛选器被改动后渲染 children。
 */
interface IKpiFilterGuardProps {
  showWhenDefault?: boolean;
  children: React.ReactNode;
  // data 来自父级 Service/Container 合并结果，内部只做透传，此处用 unknown 以避免绑定具体结构
  data?: unknown;
  // changeData 的 value 由具体子组件决定
  changeData?: (key: string, value: unknown) => void;
  // config 由上层注册表注入（可能含 props 等子字段）
  config?: { props?: Record<string, unknown>; } & Record<string, unknown>;
  // 其他由 PageContainer 注入的 props 会透传给 children
  [key: string]: unknown;
}

const ALL_DEFAULT = '255';

// 判断单个维度是否是默认值（val 形态：undefined / string[] / { markets?: string[]; regions?: unknown[]; }）
const isDimensionDefault = (val: unknown): boolean => {
  if (!val) return true;
  if (Array.isArray(val)) {
    return val.length === 0 || (val.length === 1 && val[0] === ALL_DEFAULT);
  }
  // region 是 { markets: [...], regions: [...] } 结构
  if (typeof val === 'object') {
    const obj = val as { markets?: unknown; regions?: unknown; };
    const markets = obj.markets;
    if (markets && Array.isArray(markets) && !(markets.length === 0 || (markets.length === 1 && markets[0] === ALL_DEFAULT))) {
      return false;
    }
    const regions = obj.regions;
    if (regions && Array.isArray(regions) && regions.length > 0) {
      return false;
    }
    return true;
  }
  return true;
};

const KpiFilterGuard: React.FC<IKpiFilterGuardProps> = (props) => {
  const {
    children,
    data,
    changeData,
    config,
    ...restProps
  } = props;

  // config 已在 interface 中声明，直接读取其 props 子字段
  const mergedProps = { ...props, ...(config?.props || {}) };
  const resolvedShowWhenDefault = (mergedProps.showWhenDefault as boolean | undefined) ?? true;

  const { filterVal } = useContext(FilterContext) || {};

  const isDefault = useMemo(() => {
    if (!filterVal) return true;

    // 1. 日期比较：rangeTime vs defaultRangeTime
    const rt = (filterVal.rangeTime as unknown[] | undefined) || [];
    const drt = (filterVal.defaultRangeTime as unknown[] | undefined) || [];
    if (drt.length >= 2 && rt.length >= 2) {
      if (String(rt[0]) !== String(drt[0]) || String(rt[1]) !== String(drt[1])) {
        return false;
      }
    }

    // 2. 维度筛选器
    const dimensionKeys = ['zone', 'system', 'channel', 'lang', 'region'];
    for (const key of dimensionKeys) {
      if (!isDimensionDefault(filterVal[key])) return false;
    }

    // 3. custom dimension（ua_network / campaign 等，可能是 { ua_network: [...] } 结构）
    const customDimensionKeys = ['ua_network', 'campaign'];
    for (const key of customDimensionKeys) {
      const val = filterVal[key];
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        if (!isDimensionDefault((val as Record<string, unknown>)[key])) return false;
      } else if (!isDimensionDefault(val)) {
        return false;
      }
    }

    return true;
  }, [filterVal]);

  const shouldRender = resolvedShowWhenDefault ? isDefault : !isDefault;

  if (!shouldRender) return null;

  return (
    <>
      {React.Children.map(children, (child) => {
        // 非有效 React 元素（文本节点、null 等）原样返回
        if (!React.isValidElement(child)) {
          return child;
        }
        const injectedProps: React.Attributes & Record<string, unknown> = {
          data,
          changeData,
          ...restProps,
        };
        return React.cloneElement(child, injectedProps);
      })}
    </>
  );
};

export default KpiFilterGuard;
